import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import LoadingSpinner from './LoadingSpinner';
import type {
  KnowledgeAgentScope,
  KnowledgeBrief,
  KnowledgeBriefVersionSummary,
  KnowledgeProposal,
  KnowledgeProposalStatus,
  KnowledgeProposalType,
  WebSocketEvent,
} from '../types';

const TYPE_META: Record<
  KnowledgeProposalType,
  { label: string; cls: string }
> = {
  heuristic: {
    label: 'Rule',
    cls: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  },
  misconception: {
    label: 'Common misconception',
    cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  },
  expert_voice: {
    label: 'Expert view',
    cls: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300',
  },
  contrarian: {
    label: 'Contested',
    cls: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300',
  },
  fact: {
    label: 'Fact',
    cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  },
  terminology: {
    label: 'Term',
    cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  },
  relationship: {
    label: 'Relationship',
    cls: 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300',
  },
};

const TYPE_ORDER: KnowledgeProposalType[] = [
  'heuristic',
  'misconception',
  'expert_voice',
  'contrarian',
  'fact',
  'terminology',
  'relationship',
];

const SCOPE_LABEL: Record<KnowledgeAgentScope, string> = {
  project: 'project',
  category: 'category',
  topic: 'topic',
};

interface KnowledgeBriefPanelProps {
  scope: KnowledgeAgentScope;
  scopeId: number;
  scopeName?: string;
}

export default function KnowledgeBriefPanel({
  scope,
  scopeId,
  scopeName,
}: KnowledgeBriefPanelProps) {
  const { subscribe } = useWebSocket();

  const [brief, setBrief] = useState<KnowledgeBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [groupBy, setGroupBy] = useState<'type' | 'source'>('type');
  const [editingProposalId, setEditingProposalId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  // Deep-link focus: if `?focus=<proposal_id>` is in the URL, scroll to that
  // row and flash-highlight it so the user can find what they came to delete.
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get('focus');
  const focusedProposalId = focusParam ? parseInt(focusParam, 10) : null;
  const [flashId, setFlashId] = useState<number | null>(null);

  // Scroll/flash the focused proposal once the brief loads. Used by the
  // page-builder research panel's "edit in corpus" deep link.
  useEffect(() => {
    if (!focusedProposalId || !brief) return;
    const exists = brief.proposals.some((p) => p.id === focusedProposalId);
    if (!exists) return;
    const el = document.getElementById(`proposal-${focusedProposalId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashId(focusedProposalId);
      const t = setTimeout(() => setFlashId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [focusedProposalId, brief]);

  const [editingDeep, setEditingDeep] = useState(false);
  const [deepText, setDeepText] = useState('');
  const [addingRule, setAddingRule] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [savingRule, setSavingRule] = useState(false);

  // Version history
  const [versions, setVersions] = useState<KnowledgeBriefVersionSummary[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);

  const fetchBrief = useCallback(async () => {
    setError(null);
    try {
      const data = await api.getKnowledgeBrief(scope, scopeId);
      setBrief(data);
      setDeepText(data.deep_knowledge ?? '');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load knowledge brief');
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => {
    setLoading(true);
    fetchBrief();
  }, [fetchBrief]);

  // Subscribe to live events: when the chat-based planner commits a
  // round (applies suggestions, which materialize as proposals on the
  // brief), the new proposals appear on a `knowledge_plan` replied
  // event — refetch so the proposal list stays current.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (
        event.event_type === 'knowledge_plan' &&
        event.scope === scope &&
        event.scope_id === scopeId &&
        (event.status === 'replied' || event.status === 'failed')
      ) {
        fetchBrief();
      }
    });
  }, [subscribe, scope, scopeId, fetchBrief]);

  const [resetting, setResetting] = useState(false);
  const handleReset = async () => {
    const ok = window.confirm(
      `Reset all knowledge for this ${scope}?\n\n` +
        'This deletes every proposal (including your edits + rejections), ' +
        'and clears the bedrock rules, deep knowledge, and sources list. ' +
        'The agent will start from scratch on the next run.\n\n' +
        'This cannot be undone.'
    );
    if (!ok) return;
    setResetting(true);
    try {
      const fresh = await api.resetKnowledgeBrief(scope, scopeId);
      setBrief(fresh);
      setDeepText(fresh.deep_knowledge ?? '');
    } catch (e: any) {
      alert(e?.message ?? 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const fetchVersions = useCallback(async () => {
    try {
      const resp = await api.listKnowledgeBriefVersions(scope, scopeId);
      setVersions(resp.items);
    } catch { /* best-effort */ }
  }, [scope, scopeId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions, brief?.version_number]);

  const handleRevert = async (versionId: number) => {
    if (!window.confirm('Revert to this version? A new version will be created with the reverted content.')) return;
    setRevertingId(versionId);
    try {
      const fresh = await api.revertKnowledgeBriefVersion(scope, scopeId, versionId);
      setBrief(fresh);
      setDeepText(fresh.deep_knowledge ?? '');
      fetchVersions();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to revert');
    } finally {
      setRevertingId(null);
    }
  };

  const startEditProposal = (p: KnowledgeProposal) => {
    setEditingProposalId(p.id);
    setEditingText(p.text);
  };

  const saveProposalEdit = async (p: KnowledgeProposal) => {
    if (!editingText.trim() || editingText === p.text) {
      setEditingProposalId(null);
      return;
    }
    try {
      await api.updateKnowledgeProposal(p.id, { text: editingText.trim() });
      setEditingProposalId(null);
      await fetchBrief();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to update');
    }
  };

  const setProposalStatus = async (
    p: KnowledgeProposal,
    status: KnowledgeProposalStatus
  ) => {
    try {
      await api.updateKnowledgeProposal(p.id, { status });
      await fetchBrief();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to update');
    }
  };

  const deleteProposal = async (p: KnowledgeProposal) => {
    try {
      await api.deleteKnowledgeProposal(p.id);
      await fetchBrief();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to delete');
    }
  };

  const saveDeepKnowledge = async () => {
    if (!brief) return;
    try {
      await api.updateKnowledgeBrief(brief.id, { deep_knowledge: deepText || null });
      setEditingDeep(false);
      await fetchBrief();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to save');
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error}
      </div>
    );
  }

  if (!brief) return null;

  const proposalsByType = TYPE_ORDER.map((t) => ({
    type: t,
    items: brief.proposals.filter((p) => p.proposal_type.toLowerCase() === t),
  }));
  const acceptedCount = brief.proposals.filter(
    (p) => p.status !== 'rejected'
  ).length;

  // Group proposals by source URL for the "by source" view. A proposal with
  // multiple source_urls appears under each. Proposals with no source_urls go
  // into the synthetic "no_source" bucket. Sources are listed by descending
  // count so the heaviest contributors come first.
  const NO_SOURCE = '__no_source__';
  const proposalsBySource: { source: string; items: KnowledgeProposal[] }[] = (() => {
    const map = new Map<string, KnowledgeProposal[]>();
    for (const p of brief.proposals) {
      const urls = p.source_urls.length > 0 ? p.source_urls : [NO_SOURCE];
      for (const u of urls) {
        const list = map.get(u) ?? [];
        list.push(p);
        map.set(u, list);
      }
    }
    return Array.from(map.entries())
      .map(([source, items]) => ({ source, items }))
      .sort((a, b) => {
        // "no_source" bucket always last.
        if (a.source === NO_SOURCE) return 1;
        if (b.source === NO_SOURCE) return -1;
        return b.items.length - a.items.length;
      });
  })();

  // Lookup: source URL → KnowledgeSourceEntry (so source-mode headers can show
  // method = crawl|search and the appropriate Read/Re-read button label).
  const sourceMetaByUrl = new Map(
    (brief.deep_knowledge_sources ?? []).map((s) => [s.url, s])
  );

  const renderProposalCard = (p: KnowledgeProposal, opts?: { showTypeBadge?: boolean }) => (
    <li
      key={p.id}
      id={`proposal-${p.id}`}
      className={`p-3 border rounded transition-colors ${
        flashId === p.id
          ? 'bg-yellow-100 dark:bg-yellow-900/40 border-yellow-400 dark:border-yellow-700 ring-2 ring-yellow-300'
          : p.status === 'rejected'
          ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 border-dashed'
          : p.human_modified_at
          ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}
    >
      {editingProposalId === p.id ? (
        <div className="space-y-2">
          <textarea
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            rows={3}
            className="w-full px-2 py-1 border rounded text-sm"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => saveProposalEdit(p)}
              className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
            >
              Save
            </button>
            <button
              onClick={() => setEditingProposalId(null)}
              className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={`text-sm ${p.status === 'rejected' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
            {p.text}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
            {opts?.showTypeBadge && (
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                  TYPE_META[p.proposal_type.toLowerCase() as KnowledgeProposalType]?.cls ?? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {TYPE_META[p.proposal_type.toLowerCase() as KnowledgeProposalType]?.label ?? p.proposal_type}
              </span>
            )}
            {p.confidence != null && (
              <span>confidence {Math.round(p.confidence * 100)}%</span>
            )}
            {p.source_urls.length > 0 && groupBy !== 'source' && (
              <SourcePills urls={p.source_urls} />
            )}
            {p.status === 'edited' && (
              <span className="text-amber-700 dark:text-amber-300">edited</span>
            )}
            {p.status === 'rejected' && (
              <span className="text-red-600 dark:text-red-400 font-medium">rejected</span>
            )}
            {p.human_modified_at && p.status === 'accepted' && (
              <span className="text-gray-400 dark:text-gray-500">[status: {p.status}]</span>
            )}
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => startEditProposal(p)}
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Edit
              </button>
              {p.status === 'rejected' ? (
                <button
                  onClick={() => setProposalStatus(p, 'accepted')}
                  className="text-emerald-700 dark:text-emerald-300 hover:underline"
                >
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => setProposalStatus(p, 'rejected')}
                  className="text-red-600 dark:text-red-400 hover:underline"
                >
                  Reject
                </button>
              )}
              <button
                onClick={() => deleteProposal(p)}
                className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </li>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Knowledge - {SCOPE_LABEL[scope]}
            {scopeName ? ` · ${scopeName}` : ''}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {acceptedCount} active proposal{acceptedCount === 1 ? '' : 's'}
            {brief.last_generated_at
              ? ` · last run ${new Date(brief.last_generated_at).toLocaleString()}`
              : ' · never run'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={resetting}
            className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 disabled:opacity-60"
            title="Delete all proposals, bedrock rules, deep knowledge, and sources"
          >
            {resetting ? 'Resetting…' : 'Reset'}
          </button>
        </div>
      </div>

      {/* Trusted source URLs — saved on the scope as a starting list the
          chat planner can pull from. The deprecated agent's
          run-and-crawl flow is gone; suggesting + applying knowledge
          now happens in the Knowledge agent slideout. */}
      <TrustedSourceUrlsEditor scope={scope} scopeId={scopeId} />

      {/* WRITE ONE YOURSELF. Until this existed, the only way to record a rule
          was to ask the chat planner to suggest it and then apply the
          suggestion — so knowledge you already had could not be typed in. */}
      <div className="mb-4">
        {addingRule ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const text = newRule.trim();
              if (!text || savingRule) return;
              setSavingRule(true);
              try {
                await api.createKnowledgeProposal(scope, scopeId, { text });
                setNewRule('');
                setAddingRule(false);
                await fetchBrief();
              } catch (err) {
                setError(
                  err instanceof Error ? err.message : 'Could not add that rule',
                );
              } finally {
                setSavingRule(false);
              }
            }}
            className="flex gap-2"
          >
            <input
              autoFocus
              type="text"
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setAddingRule(false);
                  setNewRule('');
                }
              }}
              placeholder="e.g. SRAM/Shimano drivetrains only; others are a red flag"
              className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-blue-400 dark:border-blue-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={!newRule.trim() || savingRule}
              className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingRule ? 'Adding…' : 'Add'}
            </button>
          </form>
        ) : (
          <button
            onClick={() => setAddingRule(true)}
            className="px-3 py-1.5 text-sm rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-400 dark:hover:border-indigo-600"
          >
            + Write a rule
          </button>
        )}
      </div>

      {/* Proposals — switch between "by type" (today's view) and "by source" */}
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Group by:</span>
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            {(['type', 'source'] as const).map((g, i) => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-2 py-0.5 text-xs capitalize ${
                  groupBy === g
                    ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300 font-medium'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                } ${i > 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {groupBy === 'type' &&
          proposalsByType.map(({ type, items }) => (
            <div key={type}>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded ${TYPE_META[type].cls}`}
                >
                  {TYPE_META[type].label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {items.length} proposal{items.length === 1 ? '' : 's'}
                </span>
              </div>
              {items.length === 0 ? (
                <div className="text-sm text-gray-400 dark:text-gray-500 italic pl-2">
                  None yet - run the agent to generate.
                </div>
              ) : (
                <ul className="space-y-2">{items.map((p) => renderProposalCard(p))}</ul>
              )}
            </div>
          ))}

        {groupBy === 'source' && proposalsBySource.length === 0 && (
          <div className="text-sm text-gray-400 dark:text-gray-500 italic">
            No proposals yet - run the agent to generate.
          </div>
        )}

        {groupBy === 'source' &&
          proposalsBySource.map(({ source, items }) => {
            const meta = source !== NO_SOURCE ? sourceMetaByUrl.get(source) : undefined;
            const wasCrawled = meta?.method === 'crawl';
            const wasSearched = meta?.method === 'search';
            return (
              <details key={source} className="group">
                <summary className="flex items-center gap-2 flex-wrap cursor-pointer select-none text-sm py-1 px-2 -ml-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 list-none [&::-webkit-details-marker]:hidden">
                  <span className="text-gray-400 dark:text-gray-500 transition-transform group-open:rotate-90">▶</span>
                  {source === NO_SOURCE ? (
                    <span
                      className="px-2 py-0.5 text-sm font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      title="Proposals without a source URL - typed manually, or emitted by the agent without citing a URL"
                    >
                      No source
                    </span>
                  ) : (
                    <span
                      className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm truncate max-w-[400px] font-medium"
                      title={source}
                    >
                      {_safeHost(source)}
                    </span>
                  )}
                  {(wasCrawled || wasSearched) && (
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        wasCrawled
                          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      }`}
                      title={
                        wasCrawled
                          ? 'Full page content was read'
                          : 'Only saw title and snippet from search results'
                      }
                    >
                      {wasCrawled ? 'read in full' : 'snippet only'}
                    </span>
                  )}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {items.length} proposal{items.length === 1 ? '' : 's'}
                  </span>
                </summary>
                <ul className="space-y-2 ml-6 mt-1">
                  {items.map((p) => renderProposalCard(p, { showTypeBadge: true }))}
                </ul>
              </details>
            );
          })}
      </div>

      {/* All sources the agent has touched — both fully read and snippet-only.
          Visible whenever any source exists. In discover-only runs this list
          is the primary artifact; in full runs it's the canonical record of
          where the proposals came from (plus searched-but-not-read URLs that
          aren't tied to any proposal yet). */}
      {brief.deep_knowledge_sources && brief.deep_knowledge_sources.length > 0 && (
        <div className="border-t pt-5">
          <DeepKnowledgeSources sources={brief.deep_knowledge_sources} />
        </div>
      )}

      {/* Deep knowledge */}
      <div className="border-t pt-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900 dark:text-gray-100">Deep knowledge</h4>
          {!editingDeep && (
            <button
              onClick={() => setEditingDeep(true)}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        {editingDeep ? (
          <div className="space-y-2">
            <textarea
              value={deepText}
              onChange={(e) => setDeepText(e.target.value)}
              rows={20}
              className="w-full px-3 py-2 border rounded text-sm font-mono"
              placeholder="Long-form domain knowledge (markdown)…"
            />
            <div className="flex gap-2">
              <button
                onClick={saveDeepKnowledge}
                className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingDeep(false);
                  setDeepText(brief.deep_knowledge ?? '');
                }}
                className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : brief.deep_knowledge ? (
          <pre className="text-xs whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 max-h-96 overflow-auto">
            {brief.deep_knowledge}
          </pre>
        ) : (
          <div className="text-sm text-gray-400 dark:text-gray-500 italic">
            None yet - run the agent or write it manually.
          </div>
        )}

        {/* Version history */}
        {versions.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1"
            >
              <svg className={`w-3 h-3 transition-transform ${showVersions ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Version history ({versions.length})
            </button>
            {showVersions && (
              <div className="mt-2 space-y-1">
                {versions.map((v) => {
                  const isCurrent = v.version_number === brief.version_number;
                  return (
                    <div
                      key={v.id}
                      className={`flex items-center justify-between px-3 py-2 rounded text-sm ${isCurrent ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800' : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-gray-700 dark:text-gray-300">v{v.version_number}</span>
                        {v.source && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            v.source === 'AGENT_RUN' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                            v.source === 'REFINEMENT' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                            v.source === 'MANUAL_EDIT' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                            'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                          }`}>
                            {v.source === 'AGENT_RUN' ? 'Agent' :
                             v.source === 'REFINEMENT' ? 'Refine' :
                             v.source === 'MANUAL_EDIT' ? 'Edit' : 'Revert'}
                          </span>
                        )}
                        {v.source_description && (
                          <span className="text-gray-500 dark:text-gray-400 truncate" title={v.source_description}>
                            {v.source_description.length > 50 ? v.source_description.slice(0, 50) + '…' : v.source_description}
                          </span>
                        )}
                        <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {new Date(v.created_at).toLocaleDateString()}
                        </span>
                        {isCurrent && <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium">current</span>}
                      </div>
                      {!isCurrent && (
                        <button
                          onClick={() => handleRevert(v.id)}
                          disabled={revertingId === v.id}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 flex-shrink-0 ml-2"
                        >
                          {revertingId === v.id ? '…' : 'Revert'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DeepKnowledgeSources — audit trail for deep_knowledge.
// Each row shows the URL, a hostname link, the method badge (crawl/search),
// and a truncated excerpt so users can verify claims at a glance.
// -----------------------------------------------------------------------------

function _safeHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function SourcePills({ urls }: { urls: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const VISIBLE = 3;
  const visible = expanded ? urls : urls.slice(0, VISIBLE);
  const overflow = urls.length - VISIBLE;
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {visible.map((url, i) => (
        <a
          key={`${url}-${i}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          title={url}
          className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-[11px] truncate max-w-[180px]"
        >
          {_safeHost(url)}
        </a>
      ))}
      {!expanded && overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 text-[11px]"
          title={`Show ${overflow} more source${overflow === 1 ? '' : 's'}`}
        >
          +{overflow}
        </button>
      )}
    </span>
  );
}

function DeepKnowledgeSources({
  sources,
}: {
  sources: KnowledgeBrief['deep_knowledge_sources'];
}) {
  if (!sources || sources.length === 0) return null;

  const crawledCount = sources.filter((s) => s.method === 'crawl').length;
  const searchedCount = sources.length - crawledCount;

  const safeHost = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  return (
    <details className="mt-3" open>
      <summary className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer select-none">
        Sources used ({sources.length})
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
          {crawledCount} read · {searchedCount} searched
        </span>
      </summary>
      <ol className="mt-2 space-y-2 text-xs">
        {sources.map((s, i) => (
          <li
            key={`${s.url}-${i}`}
            className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-400 dark:text-gray-500 font-mono">[{i + 1}]</span>
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  s.method === 'crawl'
                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                }`}
                title={s.method === 'crawl' ? 'Full page content was read' : 'Only saw title and snippet from search results'}
              >
                {s.method === 'crawl' ? 'read in full' : 'snippet only'}
              </span>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                title={s.url}
              >
                {safeHost(s.url)}
              </a>
            </div>
            {s.title && (
              <div className="mt-1 text-gray-800 dark:text-gray-200 truncate" title={s.title}>
                {s.title}
              </div>
            )}
            {s.excerpt && (
              <div className="mt-1 text-gray-500 dark:text-gray-400 line-clamp-3 whitespace-pre-wrap">
                {s.excerpt}
              </div>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}

// -----------------------------------------------------------------------------
// TrustedSourceUrlsEditor — chip-input for the scope's trusted_source_urls.
// Saves on every add/remove via the scope's own update endpoint.
// -----------------------------------------------------------------------------

function TrustedSourceUrlsEditor({
  scope,
  scopeId,
}: {
  scope: KnowledgeAgentScope;
  scopeId: number;
}) {
  const [urls, setUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let list: string[] = [];
        if (scope === 'project') {
          const p = await api.getProject(scopeId);
          list = p.trusted_source_urls ?? [];
        } else if (scope === 'category') {
          const c = await api.getCategory(scopeId);
          list = c.trusted_source_urls ?? [];
        } else {
          const t = await api.getTopic(scopeId);
          list = t.trusted_source_urls ?? [];
        }
        setUrls(list);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load trusted URLs');
      } finally {
        setLoading(false);
      }
    })();
  }, [scope, scopeId]);

  const persist = async (next: string[]) => {
    setSaving(true);
    setError(null);
    try {
      if (scope === 'project') {
        await api.updateProject(scopeId, { trusted_source_urls: next });
      } else if (scope === 'category') {
        await api.updateCategory(scopeId, { trusted_source_urls: next });
      } else {
        await api.updateTopic(scopeId, { trusted_source_urls: next });
      }
      setUrls(next);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addUrl = () => {
    const candidate = input.trim();
    setInput('');
    if (!candidate) return;
    if (!/^https?:\/\//i.test(candidate)) {
      setError('URL must start with http:// or https://');
      return;
    }
    if (urls.includes(candidate)) return;
    persist([...urls, candidate]);
  };

  const removeUrl = (url: string) => persist(urls.filter((u) => u !== url));

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Trusted source URLs
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          forums, expert blogs, vendor docs - agent crawls these first
        </span>
        {saving && (
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Saving…</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {loading ? (
          <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
        ) : urls.length === 0 ? (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">None yet</span>
        ) : (
          urls.map((url) => (
            <span
              key={url}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded max-w-full"
            >
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="truncate max-w-[260px] hover:underline"
                title={url}
              >
                {url}
              </a>
              <button
                type="button"
                onClick={() => removeUrl(url)}
                className="icon-btn text-blue-500 hover:text-blue-700 dark:hover:text-blue-300"
                aria-label={`Remove ${url}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <input
        type="url"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addUrl();
          }
        }}
        onBlur={addUrl}
        placeholder="https://example.com/forum-thread - press Enter"
        disabled={loading || saving}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
      />

      {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
    </div>
  );
}
